import { Box, Stack, TextField, outlinedInputClasses } from '@mui/material'
import { C1IconButton } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1Icon } from 'components/core/icons/C1Icon'
import { ArrowUp } from 'lucide-react'
import { FC, memo } from 'react'
import { useIntl } from 'react-intl'
import { ChatVariant } from './ChatPanel'
import { ChatStatus } from './hooks/useChat'

type ChatInputProps = {
    input: string
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    handleSubmit: () => void
    status: ChatStatus
    variant?: ChatVariant
    placeholder?: string
}

/**
 * ChatInput provides the text input and submit button for chat interfaces.
 *
 * Variants:
 * - 'default': Full-width multiline input for main chat
 * - 'mini': Compact single-line input for embedded chat
 */
export const ChatInput: FC<ChatInputProps> = memo(({
    input,
    handleInputChange,
    handleSubmit,
    status,
    variant = 'default',
    placeholder,
}) => {
    const intl = useIntl()
    const isMini = variant === 'mini'

    const defaultPlaceholder = intl.formatMessage({
        id: 'component_chat_thread_input_placeholder',
        defaultMessage: 'Ask anything',
    })

    if (isMini) {
        return <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                p: 1,
                // borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={input}
                onChange={handleInputChange}
                disabled={status !== 'ready'}
                onKeyDown={(e): void => {
                    if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
                        e.preventDefault()
                        handleSubmit()
                    }
                }}
                placeholder={placeholder ?? defaultPlaceholder}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: C1BorderRadius.regular,
                    },
                }}
            />
            <C1IconButton
                variant="outlined"
                size="small"
                onClick={handleSubmit}
                disabled={!input.trim() || status !== 'ready'}
                aria-label={intl.formatMessage({
                    id: 'component_chat_thread_send_message',
                    defaultMessage: 'Send message',
                })}
            >
                <C1Icon icon={ArrowUp} size="sm" />
            </C1IconButton>
        </Stack>
    }

    // Default variant
    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                p: 2,
                border: 1,
                borderColor: 'border.primary',
                backgroundColor: 'background.default',
                borderRadius: C1BorderRadius.large,
                mt: 1,
                mb: 2,
                width: '100%',
                maxWidth: '1200px',
                alignSelf: 'center',
                alignItems: input ? 'flex-end' : 'center',
                zIndex: 100,
            }}
        >
            <TextField
                autoFocus
                fullWidth
                multiline
                size="small"
                variant="outlined"
                value={input}
                onChange={handleInputChange}
                disabled={status !== 'ready'}
                onKeyDown={(e): void => {
                    if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
                        e.preventDefault()
                        handleSubmit()
                    }
                }}
                sx={{
                    [`& .${outlinedInputClasses.input}`]: {
                        borderRadius: 0,
                    },
                    [`& .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]: {
                        border: 'none',
                        borderRadius: 0,
                    },
                }}
                placeholder={placeholder ?? defaultPlaceholder}
            />
            <Box sx={{
                minHeight: '38px', // Match the height of the input field
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            >
                <C1IconButton
                    variant="outlined"
                    onClick={handleSubmit}
                    disabled={!input.trim() || status !== 'ready'}
                    aria-label={intl.formatMessage({
                        id: 'component_chat_thread_send_message',
                        defaultMessage: 'Send message',
                    })}
                >
                    <C1Icon icon={ArrowUp} size="md" />
                </C1IconButton>
            </Box>
        </Stack>
    )
})
